import React, { useEffect, useMemo, useRef } from 'react'
import AppCombobox from '../../../../components/AppCombobox'
import ReportChartRenderer from '../components/ReportChartRenderer'
import { StepHeader, StepCard, INPUT_CLASS, LABEL_CLASS } from '../components/StepLayout'

const VIS_TYPES = [
  { id: 'TABLE', label: 'Table Only', icon: 'table_chart', desc: 'Standard tabular layout' },
  { id: 'BAR', label: 'Bar Chart', icon: 'bar_chart', desc: 'Comparative bars' },
  { id: 'LINE', label: 'Line Chart', icon: 'show_chart', desc: 'Trend timeline' },
  { id: 'PIE', label: 'Pie Chart', icon: 'pie_chart', desc: 'Distribution slices' },
  { id: 'KPI', label: 'KPI Cards', icon: 'speed', desc: 'Top summary metrics' },
  { id: 'CHART_TABLE', label: 'Chart+Table', icon: 'dashboard', desc: 'Chart above table' },
]

// How many metrics each visualization type reasonably supports.
const MAX_METRICS_BY_TYPE = { PIE: 1, KPI: 4, BAR: 4, LINE: 4, CHART_TABLE: 4 }

export default function Step8Visualization({
  visualization,
  setVisualization,
  selectedFields = [],
  allFields = [],
  fetchPreview,
  previewData,
  previewLoading,
  previewError,
}) {
  const fields = selectedFields.length > 0 ? selectedFields : allFields
  const metricFields = fields
  const dimensionFields = fields

  const isChartActive = visualization.type !== 'TABLE'
  const isKpi = visualization.type === 'KPI'
  const needsAxes = isChartActive && !isKpi
  const maxMetrics = MAX_METRICS_BY_TYPE[visualization.type] || 4

  const selectedMetrics = useMemo(() => {
    if (visualization.series && visualization.series.length > 0) return visualization.series
    return visualization.yAxis ? [visualization.yAxis] : []
  }, [visualization.series, visualization.yAxis])

  function setVisProp(prop, value) {
    setVisualization((prev) => ({ ...prev, [prop]: value }))
  }

  function toggleMetric(key) {
    setVisualization((prev) => {
      const current = prev.series && prev.series.length > 0 ? prev.series : prev.yAxis ? [prev.yAxis] : []
      let next
      if (current.includes(key)) {
        next = current.filter((k) => k !== key)
      } else if (maxMetrics === 1) {
        next = [key]
      } else if (current.length >= maxMetrics) {
        return prev
      } else {
        next = [...current, key]
      }
      return { ...prev, series: next, yAxis: next[0] || '' }
    })
  }

  // Live inline preview: whenever the chart configuration meaningfully
  // changes, quietly re-run the preview so the person can see the chart
  // taking shape without having to jump ahead to the Preview step.
  const debounceRef = useRef(null)
  const metricsKey = selectedMetrics.join('|')
  useEffect(() => {
    if (!fetchPreview || !needsAxes) return undefined
    if (!visualization.xAxis || selectedMetrics.length === 0) return undefined
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => {
      fetchPreview()
    }, 600)
    return () => clearTimeout(debounceRef.current)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visualization.type, visualization.xAxis, metricsKey])

  const xAxisOptions = useMemo(() => dimensionFields.map((f) => ({ value: f.key, label: f.label })), [dimensionFields])

  return (
    <div className="space-y-3 max-w-5xl mx-auto py-1">
      <StepHeader
        title="Visualization Layout & Chart Mapping"
        subtitle="Choose how the report should look — the preview updates live as you configure it."
      />

      {/* Visual Type Selector */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-2.5">
        {VIS_TYPES.map((vt) => {
          const selected = visualization.type === vt.id
          return (
            <button
              key={vt.id}
              type="button"
              onClick={() => setVisProp('type', vt.id)}
              className={`flex flex-col p-3 rounded-[8px] border text-left transition-all ${
                selected
                  ? 'border-primary bg-primary/10 ring-1 ring-primary/30 shadow-xs'
                  : 'border-surface-container-high bg-surface-container-low hover:bg-surface-container'
              }`}
            >
              <div className="flex items-center gap-1.5 mb-1.5">
                <span className={`material-symbols-outlined text-base ${selected ? 'text-primary' : 'text-slate-400'}`}>
                  {vt.icon}
                </span>
                <span className={`text-xs font-bold truncate ${selected ? 'text-primary' : 'text-on-surface'}`}>
                  {vt.label}
                </span>
              </div>
              <p className="text-[10px] text-on-surface-variant line-clamp-1">{vt.desc}</p>
            </button>
          )
        })}
      </div>

      {isChartActive && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 items-start">
          {/* ── Configuration ── */}
          <StepCard
            title={isKpi ? 'KPI Metric Selection' : 'Chart Configuration'}
            subtitle={
              isKpi
                ? 'Pick which numbers should appear as summary tiles.'
                : 'Pick what goes on each axis — the live preview updates automatically.'
            }
          >
            <div className="space-y-1">
              <label className={LABEL_CLASS}>Chart Title</label>
              <input
                type="text"
                value={visualization.title || ''}
                onChange={(e) => setVisProp('title', e.target.value)}
                placeholder="e.g., Department Revenue Breakdown"
                className={INPUT_CLASS}
              />
            </div>

            {needsAxes && (
              <div className="space-y-1 pt-1">
                <label className={LABEL_CLASS}>
                  X-Axis / Category <span className="text-error">*</span>
                </label>
                <AppCombobox
                  options={xAxisOptions}
                  value={visualization.xAxis || ''}
                  onChange={(val) => setVisProp('xAxis', val)}
                  placeholder="Select category dimension..."
                  searchPlaceholder="Search fields..."
                  triggerClassName="h-10 bg-surface text-sm"
                />
              </div>
            )}

            <div className="space-y-1.5 pt-1">
              <div className="flex items-center justify-between">
                <label className={LABEL_CLASS}>
                  {isKpi ? 'Metrics to feature' : 'Metrics (Y-Axis)'} <span className="text-error">*</span>
                </label>
                <span className="text-[10px] text-on-surface-variant">
                  {selectedMetrics.length}/{maxMetrics} selected
                </span>
              </div>
              {metricFields.length === 0 ? (
                <p className="text-[10px] text-error font-medium">No columns available to use as metrics.</p>
              ) : (
                <div className="flex flex-wrap gap-1.5">
                  {metricFields.map((f) => {
                    const active = selectedMetrics.includes(f.key)
                    const disabled = !active && selectedMetrics.length >= maxMetrics
                    return (
                      <button
                        key={f.key}
                        type="button"
                        disabled={disabled}
                        onClick={() => toggleMetric(f.key)}
                        className={`px-2.5 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
                          active
                            ? 'border-primary bg-primary text-on-primary'
                            : disabled
                            ? 'border-surface-container-high text-on-surface-variant/40 cursor-not-allowed'
                            : 'border-surface-container-high bg-surface text-on-surface hover:bg-surface-container'
                        }`}
                      >
                        {f.label}
                      </button>
                    )
                  })}
                </div>
              )}
              {maxMetrics > 1 ? (
                <p className="text-[10px] text-on-surface-variant">Pick up to {maxMetrics} metrics to compare side by side.</p>
              ) : (
                <p className="text-[10px] text-on-surface-variant">Pie charts show one metric at a time.</p>
              )}
            </div>
          </StepCard>

          {/* ── Live preview ── */}
          <StepCard
            title="Live Preview"
            subtitle="What your chart looks like right now, with your current filters applied."
            actions={
              <button
                type="button"
                onClick={() => fetchPreview?.()}
                className="p-1.5 rounded-[8px] border border-surface-container-high hover:bg-surface-container text-slate-500"
                title="Refresh preview"
              >
                <span className="material-symbols-outlined text-sm">refresh</span>
              </button>
            }
          >
            {isKpi ? (
              selectedMetrics.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-10 text-center text-xs text-on-surface-variant">
                  <span className="material-symbols-outlined text-2xl text-slate-300 mb-1">speed</span>
                  Choose metrics above to see them appear as KPI cards.
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-2">
                  {selectedMetrics.map((key) => {
                    const f = allFields.find((x) => x.key === key)
                    return (
                      <div key={key} className="p-3 bg-surface-container-low rounded-[8px] border border-surface-container-high">
                        <span className="text-[9px] uppercase font-bold text-on-surface-variant tracking-wider block truncate">
                          {f?.label || key}
                        </span>
                        <span className="text-sm font-bold text-primary mt-0.5 block">Shown as a KPI tile</span>
                      </div>
                    )
                  })}
                </div>
              )
            ) : !visualization.xAxis || selectedMetrics.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-10 text-center text-xs text-on-surface-variant">
                <span className="material-symbols-outlined text-2xl text-slate-300 mb-1">insights</span>
                Choose an X-Axis and at least one metric to see a live preview.
              </div>
            ) : previewLoading ? (
              <div className="flex flex-col items-center justify-center py-10 space-y-2">
                <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
                <p className="text-[11px] text-on-surface-variant">Building preview...</p>
              </div>
            ) : previewError ? (
              <div className="py-6 text-center text-xs text-error">{previewError}</div>
            ) : (
              <ReportChartRenderer
                visualization={visualization}
                chartData={previewData?.chartData || []}
                summaryCards={previewData?.cards || []}
                columns={previewData?.columns || []}
              />
            )}
          </StepCard>
        </div>
      )}
    </div>
  )
}
